// The contract every layer of the client keeps.
//
// The client is a purpose-built engine (the plan's Part A). A layer here
// owns CPU arrays and instance batches. It owns no scene graph, and it
// allocates nothing in the steady state. The frame loop calls `update` on
// every visible layer, then `draw` on every visible layer, in the slot order
// of the plan's A.5 table.
//
// This module is pure types and constants. It names no backend, so a test
// can read it in jsdom.

// The visible world rect and the cull margin live in `render/cull.ts`, which
// imports no engine, so the layers and the menu previews read one margin and
// one rect type (the plan's A.1).
export { CULL_MARGIN_PX, outside } from '../render/cull';
export type { ViewRect } from '../render/cull';

import type { ViewRect } from '../render/cull';
import type { Gfx } from './gfx/types';

// ---------------------------------------------------------------------------
// Instance flags
// ---------------------------------------------------------------------------

/**
 * The size or the width is in CSS px, so the mark keeps its size on screen
 * at any zoom. The shader divides by the zoom.
 *
 * Nothing in the PixiJS client is zoom-invariant, so parity means the flag
 * is off everywhere (the plan's A.1). It stays for experiments.
 */
export const F_SCREEN = 1;

/**
 * Additive: the fragment writes alpha 0.
 *
 * The one blend state is premultiplied `ONE, ONE_MINUS_SRC_ALPHA`. A
 * fragment of `(rgb·a, 0)` composites as `dst + rgb·a`, which is exactly
 * `ONE, ONE`. So additive glow needs no second blend state and no second
 * draw call — it is a per-instance flag (the plan's A.1).
 *
 * This holds in the dark identity only. In the light identity an F_ADD
 * instance is a covered ink at its alpha times `AP_GLOW_K`, because added
 * light disappears on paper (`fast/shaders/addglow.ts`, R3297).
 */
export const F_ADD = 2;

/** Segments only: round caps. Without it the cap is butt. */
export const F_ROUND = 4;

/**
 * Segments only, butt caps only: the start (`F_JOIN0`) or the end
 * (`F_JOIN1`) of the segment joins the next leg of a polyline (R3191). The
 * dash pair then carries the slope of the cut at each end, `tan` of half
 * the turn, and the shader cuts that end on the bisector of the corner.
 * Two legs that join so meet on one line with no overlap and no gap, so a
 * translucent fill of a polyline draws one alpha at each corner. A cut is
 * hard: the two legs share it, so an antialiased cut drew a seam. The
 * outer point of a corner stops at twice the half width, the miter limit of
 * `offsetLine`. A joined segment draws no dash.
 */
export const F_JOIN0 = 8;
export const F_JOIN1 = 16;

/**
 * Quads only: bits 3 to 5 hold a chart text step plus one (R1796, §17.4).
 * The quad shader scales such a quad's box and its offset from the pivot by
 * the step's entry of the `uStep` uniform, the band scale of this frame, so
 * every glyph of a word grows or shrinks about one pivot. 0 is no step.
 */
export const F_STEP_SHIFT = 3;

// ---------------------------------------------------------------------------
// Camera
// ---------------------------------------------------------------------------

/**
 * What the camera hands the shaders once a frame.
 *
 * Both matrices are `mat3` in column-major order, which is the order
 * `uniformMatrix3fv` wants with `transpose` false.
 */
export interface CameraUniforms {
  /** World px to clip space. */
  viewProj: Float32Array;
  /** Device px (`gl_FragCoord` convention, y up) to world px. */
  viewInv: Float32Array;
  /** The camera scale: CSS px for one world px. */
  zoom: number;
  /** Device px for one CSS px, capped at 2. */
  dpr: number;
  /** Device px for one world px, which is `zoom · dpr`. */
  pxPerWorld: number;
  /** The drawing surface in CSS px. */
  w: number;
  h: number;
  /** The quake shudder in CSS px. It rides in both matrices. */
  shakeX: number;
  shakeY: number;
}

// ---------------------------------------------------------------------------
// The frame
// ---------------------------------------------------------------------------

/** What every layer is told about the frame. One object, reused. */
export interface FrameInfo {
  /** The step in ms, clamped to 100 so a stall does not teleport anything. */
  dtMs: number;
  /** Seconds since the loop started. It stops while the page is hidden. */
  timeS: number;
  /** The `requestAnimationFrame` timestamp in ms. */
  now: number;
  /** The visible world rect. It carries no shake, as `main.ts` computes it. */
  view: ViewRect;
  /** `view` grown by `CULL_MARGIN_PX` on every side. */
  cull: ViewRect;
  /** The camera for this frame. */
  cam: CameraUniforms;
}

/** What one layer cost in one frame. */
export interface LayerStats {
  updateMs: number;
  drawCalls: number;
  instances: number;
}

/** What the whole frame cost. `frame.ts` sweeps it once a second. */
export interface FrameStats {
  fps: number;
  frameMs: number;
  updateMs: number;
  drawMs: number;
  /**
   * GPU ms from the timer-query ring, or null without the extension.
   *
   * The query opens BEFORE the clear and closes after the last draw call of
   * the frame, so the figure holds every device pixel the frame writes. The
   * clear is a screen-sized write, and a floor that leaves it out is not the
   * floor (R431).
   */
  gpuMs: number | null;
  drawCalls: number;
  instances: number;
  perLayer: Record<string, LayerStats>;
  contextLost: boolean;
}

/** What a layer is handed to draw with. */
export interface DrawCtx {
  /** The drawing surface, whichever backend it is. */
  gfx: Gfx;
  cam: CameraUniforms;
  frame: FrameInfo;
  stats: FrameStats;
  /** The name of the layer being drawn. Only the instrumentation reads it. */
  layer: string;
}

/**
 * One layer of the map.
 *
 * `update` does the CPU work and writes the instance buffers. `draw`
 * submits them. An invisible layer skips both (ui-map-interface.md §10.3).
 */
export interface Layer {
  readonly name: string;
  /** Advance the layer and pack its instances. No steady-state allocation. */
  update(frame: FrameInfo): void;
  /** Submit the packed instances. */
  draw(ctx: DrawCtx): void;
  setVisible(v: boolean): void;
  readonly visible: boolean;
  dispose?(): void;
}

/**
 * An empty layer. The engine fills its ordered slot table with these, so a
 * slot the plan reserves for later work costs one call that returns at once.
 */
export class NullLayer implements Layer {
  private shown = true;
  constructor(readonly name: string) {}
  update(_frame: FrameInfo): void { /* nothing to advance */ }
  draw(_ctx: DrawCtx): void { /* nothing to submit */ }
  setVisible(v: boolean): void { this.shown = v; }
  get visible(): boolean { return this.shown; }
}
